/**
 * component-product-info.js
 * Coordinator for the _product-info zone.
 *
 * Under the Section Rendering API architecture (DEV-VARIANT-STRATEGY-PIVOT in
 * DEVIATION-LOG), variant changes trigger a fetch of the updated section HTML
 * and a replacement of the [data-product-info-root] subtree. Price, SKU,
 * inventory, and other variant-reactive info-zone blocks re-render server-side.
 *
 * Public API:
 *   window.__productInfo[productId] = {
 *     root: HTMLElement,
 *     refreshSection: function (optionValueIds, focusElement) => Promise
 *   };
 *
 * Events dispatched on `document` after each successful refresh:
 *   product:variant-changed  (detail: { productId, variant, source, root })
 *
 * `detail.root` (added 2026-05-01) is the freshly-swapped
 * [data-product-info-root] element. Consumers can scope their re-init to
 * this root rather than re-querying the document.
 */

(function () {
  'use strict';

  var ROOT_ATTR = 'data-product-info-root';
  var INIT_GUARD = '__productInfoInit';

  window.__productInfo = window.__productInfo || {};

  function resolveSectionId(root) {
    var section = root.closest('[id^="shopify-section-"]');
    if (!section) return null;
    return section.id.replace(/^shopify-section-/, '');
  }

  function readSelectedVariantFromDOM(root) {
    var blob = root.querySelector('[data-selected-variant-json]');
    if (!blob) return null;
    try {
      return JSON.parse(blob.textContent);
    } catch (e) {
      return null;
    }
  }

  function refreshSection(root, optionValueIds, focusElement) {
    var productUrl = root.getAttribute('data-product-url');
    var sectionId = resolveSectionId(root);

    if (!productUrl) {
      console.warn('[product-info] missing data-product-url on root');
      return Promise.reject(new Error('missing data-product-url'));
    }
    if (!sectionId) {
      console.warn('[product-info] could not resolve section ID from DOM');
      return Promise.reject(new Error('missing section ID'));
    }

    var url = productUrl + '?section_id=' + encodeURIComponent(sectionId);
    if (optionValueIds && optionValueIds.length) {
      url += '&option_values=' + optionValueIds.map(encodeURIComponent).join(',');
    }

    // Stale-response guard: if user clicks variants quickly, only the latest
    // response should apply.
    var reqId = (root.__reqId = (root.__reqId || 0) + 1);

    return fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (root.__reqId !== reqId) return; // outdated, newer request in flight
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newRoot = doc.querySelector('[' + ROOT_ATTR + ']');
        if (!newRoot) throw new Error('fetched HTML missing [' + ROOT_ATTR + ']');

        // All three steps live in one function so they execute inside the View
        // Transition callback (when the API is supported). Listeners on
        // product:variant-changed that mutate DOM (gallery image swap, sticky
        // ATC) are then captured in the same transition snapshot, producing a
        // coordinated visual change.
        function applyAndFinalize() {
          // Capture quantity-block input values before the innerHTML swap
          // wipes them. Keyed by input id so we can match against the new
          // DOM after the swap. Generic mechanism — any block that emits an
          // input with [data-quantity-input] gets value preservation across
          // variant change.
          var preservedQty = {};
          root.querySelectorAll('[data-quantity-input]').forEach(function (inp) {
            if (inp.id) preservedQty[inp.id] = inp.value;
          });

          root.innerHTML = newRoot.innerHTML;

          // Restore focus to the originally-clicked element (if it still exists
          // in the new DOM — e.g. a variant-picker radio input).
          if (focusElement && focusElement.id) {
            var restored = root.querySelector('#' + CSS.escape(focusElement.id));
            if (restored) {
              try { restored.focus({ preventScroll: true }); } catch (e) { /* noop */ }
            }
          }

          // Restore captured quantity values, clamped against the new variant's
          // min/max attributes. If the new variant's stock is lower than the
          // preserved value, the input lands at the new max instead of the old
          // typed value.
          Object.keys(preservedQty).forEach(function (id) {
            var newInput = root.querySelector('#' + CSS.escape(id));
            if (!newInput) return;
            var oldVal = parseInt(preservedQty[id], 10);
            var newMin = parseInt(newInput.getAttribute('min'), 10) || 1;
            var newMax = parseInt(newInput.getAttribute('max'), 10) || 9999;
            if (isNaN(oldVal)) return;
            var restoredQty = Math.max(newMin, Math.min(newMax, oldVal));
            newInput.value = restoredQty;
          });

          // Build event detail from the freshly-rendered selected-variant blob.
          var detail = {
            productId: root.getAttribute('data-product-id'),
            variant: readSelectedVariantFromDOM(root),
            source: 'product-info-refresh',
            root: root
          };

          document.dispatchEvent(new CustomEvent('product:variant-changed', { detail: detail, bubbles: true }));
        }

        // View Transitions API for a smooth crossfade. Falls back to direct
        // mutation when the API isn't supported (Firefox as of Apr 2026, plus
        // older Chrome/Safari). prefers-reduced-motion is automatically honored
        // by the API — animation skipped under that preference.
        if (typeof document.startViewTransition === 'function') {
          document.startViewTransition(applyAndFinalize);
        } else {
          applyAndFinalize();
        }
      })
      .catch(function (err) {
        console.error('[product-info] variant refresh failed:', err);
      });
  }

  function init(root) {
    if (root[INIT_GUARD]) return;
    root[INIT_GUARD] = true;

    var productId = root.getAttribute('data-product-id') || 'default';
    window.__productInfo[productId] = {
      root: root,
      refreshSection: function (optionValueIds, focusElement) {
        return refreshSection(root, optionValueIds, focusElement);
      }
    };
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[' + ROOT_ATTR + ']').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-init on theme-editor section load (when merchant adds/edits the section in the editor).
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  /* ------------------------------------------------------------
     _product-price — installment pill swap.
     Event delegation on `document` so the handler survives the SRA
     innerHTML replacement of [data-product-info-root].
     Guard against double-registration when this file loads again
     after a full page nav in the editor preview.
     ------------------------------------------------------------ */
  if (!window.__productPriceInit) {
    window.__productPriceInit = true;

    document.addEventListener('click', function (e) {
      var pill = e.target.closest && e.target.closest('[data-pp-inst-pill]');
      if (pill) {
        var instRoot = pill.closest('[data-pp-inst]');
        if (instRoot) {
          var pills = instRoot.querySelectorAll('[data-pp-inst-pill]');
          pills.forEach(function (p) {
            var active = p === pill;
            p.classList.toggle('is-active', active);
            p.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          var amountEl = instRoot.querySelector('[data-pp-inst-amount]');
          var amountHtml = pill.getAttribute('data-amount-html') || '';
          if (amountEl) amountEl.innerHTML = amountHtml;
        }
        return;
      }
    });
  }

  /* ------------------------------------------------------------
     _product-variant-picker — variant change coordinator +
     accordion mutual exclusion + size-guide lazy-load trigger.
     Event delegation on `document` so handlers survive the SRA
     innerHTML replacement of [data-product-info-root].
     See PROMPT-3-SPEC.md File 6 and Phase 3.3 decisions #34–#42.
     ------------------------------------------------------------ */
  if (!window.__productVariantPickerInit) {
    window.__productVariantPickerInit = true;

    function pvpRefresh(target) {
      var picker = target.closest && target.closest('[data-pvp-block]');
      if (!picker) return;
      var info = picker.closest('[data-product-info-root]');
      if (!info) return;
      var pid = info.getAttribute('data-product-id');
      var coord = pid && window.__productInfo && window.__productInfo[pid];
      if (!coord || typeof coord.refreshSection !== 'function') return;

      var ids = [];
      picker.querySelectorAll('[data-pvp-value]:checked').forEach(function (el) {
        var id = el.getAttribute('data-option-value-id');
        if (id) ids.push(id);
      });
      picker.querySelectorAll('[data-pvp-dropdown]').forEach(function (sel) {
        var opt = sel.options[sel.selectedIndex];
        if (opt) {
          var id = opt.getAttribute('data-option-value-id');
          if (id) ids.push(id);
        }
      });

      coord.refreshSection(ids, target);
    }

    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('[data-pvp-value]')) {
        if (!t.disabled) pvpRefresh(t);
        return;
      }
      if (t.matches('[data-pvp-dropdown]')) {
        pvpRefresh(t);
      }
    });

    // Post-fetch authoritative URL update. Fired by the SRA coordinator after
    // the DOM swap completes; the picker root now carries the freshly-rendered
    // data-variant-url-update attribute, so a merchant toggle change takes
    // effect on the next refresh.
    document.addEventListener('product:variant-changed', function (e) {
      var pid = e.detail && e.detail.productId;
      if (!pid) return;
      var coord = window.__productInfo && window.__productInfo[pid];
      if (!coord || !coord.root) return;
      var picker = coord.root.querySelector('[data-pvp-block]');
      if (!picker) return;
      if (picker.getAttribute('data-variant-url-update') !== 'on') return;
      var variant = e.detail.variant;
      if (!variant || !variant.id) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(null, '', url.toString());
      } catch (err) {
        // URL mutation failure is non-fatal — picker UI still reflects selection.
      }
    });

    // Accordion mutual exclusion — close sibling <details> when one opens.
    // `toggle` does not bubble, so capture-phase registration is required.
    document.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d || !d.matches) return;
      if (!d.matches('.pvp-option__accordion')) return;
      if (!d.open) return;
      var picker = d.closest('[data-pvp-block]');
      if (!picker) return;
      picker.querySelectorAll('.pvp-option__accordion[open]').forEach(function (o) {
        if (o !== d) o.open = false;
      });
    }, true);

  }
})();

/* ============================================================
   _product-buy-buttons — Phase 3.5 P16
   Submit handler + cart drawer integration + +/- handlers +
   post-ATC SRA refresh + cap announcements.
   Document-level event delegation — survives SRA innerHTML swap.
   ============================================================ */

(function () {
  'use strict';

  if (window.__productBuyButtonsInit) return;
  window.__productBuyButtonsInit = true;

  // ---------- Helpers ----------

  function announce(root, message) {
    if (window.theme && window.theme.utilities && typeof window.theme.utilities.announce === 'function') {
      window.theme.utilities.announce(message);
      return;
    }
    if (!root) return;
    var live = root.querySelector('[data-pp-live-region]');
    if (!live) return;
    live.textContent = '';
    void live.offsetHeight;
    live.textContent = message;
    setTimeout(function () { live.textContent = ''; }, 1500);
  }

  function readCurrentOptionValueIds(infoRoot) {
    var ids = [];
    var picker = infoRoot.querySelector('[data-pvp-block]');
    if (picker) {
      picker.querySelectorAll('[data-pvp-value]:checked').forEach(function (el) {
        var id = el.getAttribute('data-option-value-id');
        if (id) ids.push(id);
      });
      picker.querySelectorAll('[data-pvp-dropdown]').forEach(function (sel) {
        var opt = sel.options[sel.selectedIndex];
        if (opt) {
          var id = opt.getAttribute('data-option-value-id');
          if (id) ids.push(id);
        }
      });
    }
    if (ids.length) return ids;
    var blob = infoRoot.querySelector('[data-selected-variant-json]');
    if (!blob) return [];
    try {
      var v = JSON.parse(blob.textContent);
      if (v && Array.isArray(v.option_values)) {
        return v.option_values.map(function (o) { return o.id; }).filter(Boolean);
      }
    } catch (e) { /* fall through */ }
    return [];
  }

  function refreshInfoZone(infoRoot) {
    if (!infoRoot) return Promise.resolve();
    var productId = infoRoot.getAttribute('data-product-id');
    if (!productId) return Promise.resolve();
    var coord = window.__productInfo && window.__productInfo[productId];
    if (!coord || typeof coord.refreshSection !== 'function') return Promise.resolve();
    return coord.refreshSection(readCurrentOptionValueIds(infoRoot));
  }

  function updatePriceLabel(root, qty) {
    if (!root) return;
    var unitPrice = parseInt(root.getAttribute('data-unit-price'), 10);
    if (isNaN(unitPrice)) return;
    var priceSpan = root.querySelector('[data-pp-price-amount]');
    if (!priceSpan) return;
    var totalCents = unitPrice * qty;
    if (window.theme && window.theme.utilities && typeof window.theme.utilities.formatMoney === 'function') {
      priceSpan.textContent = window.theme.utilities.formatMoney(totalCents);
    }
  }

  // ---------- Submit handler ----------

  async function handleBuyButtonsSubmit(event) {
    if (!event.target.matches || !event.target.matches('form.pp-buy-buttons__form')) return;
    event.preventDefault();

    var form = event.target;
    var root = form.closest('[data-pp-buy-buttons]');
    if (!root) return;
    var infoRoot = form.closest('[data-product-info-root]');
    if (!infoRoot) return;

    var atc = root.querySelector('[data-pp-atc]');
    var spinner = root.querySelector('.pp-buy-buttons__atc-spinner');
    var errorZone = root.querySelector('[data-pp-error]');
    var errorMsg = root.querySelector('[data-pp-error-msg]');
    var variantInput = root.querySelector('[data-pp-variant-id]');
    var qtyInput = root.querySelector('[data-quantity-input]');

    var variantId = variantInput ? parseInt(variantInput.value, 10) : NaN;
    if (!variantId || isNaN(variantId)) {
      if (errorMsg) errorMsg.textContent = 'Product unavailable';
      if (errorZone) errorZone.removeAttribute('hidden');
      announce(root, 'Product unavailable');
      return;
    }

    var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!qty || isNaN(qty) || qty < 1) qty = 1;

    if (qtyInput) {
      var min = parseInt(qtyInput.getAttribute('min'), 10) || 1;
      var maxAttr = qtyInput.getAttribute('max');
      var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
      if (qty < min) { qty = min; qtyInput.value = qty; announce(root, 'Adjusted quantity'); }
      if (qty > max) { qty = max; qtyInput.value = qty; announce(root, 'Adjusted quantity'); }
    }

    if (atc && atc.hasAttribute('disabled')) return;

    if (atc) {
      atc.setAttribute('disabled', '');
      atc.setAttribute('data-loading', '');
    }
    if (spinner) spinner.removeAttribute('hidden');
    announce(root, 'Adding…');

    if (errorZone) errorZone.setAttribute('hidden', '');

    var drawer = document.querySelector('cart-drawer[data-atc-auto-open]');
    var drawerWillAutoOpen = !!(drawer && drawer.dataset.atcAutoOpen === 'true');

    try {
      await window.theme.cart_api.add({ id: variantId, quantity: qty });
      announce(root, 'Added to cart');
      // Restore loading state — SRA refresh used to do this implicitly via DOM
      // swap, but we no longer SRA-refresh post-add (View Transitions vs drawer
      // animation conflict). Without explicit restore, button stays disabled
      // with data-loading set forever.
      if (atc) {
        atc.removeAttribute('disabled');
        atc.removeAttribute('data-loading');
      }
      if (spinner) spinner.setAttribute('hidden', '');
      // Note: SRA refresh deliberately not called here. View Transitions API on
      // the info zone subtree conflicts visually with the cart drawer's open
      // animation (info zone flashes above drawer for ~300ms). Drawer is the
      // authoritative cart UI; PDP in-cart indicator may be briefly stale until
      // next variant change or page reload — accepted trade-off.
      if (!drawerWillAutoOpen) {
        var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
        window.location.assign(cartUrl);
      }
    } catch (err) {
      var msg = (err && err.message) ? err.message : 'Could not add to cart';
      if (errorMsg) errorMsg.textContent = msg;
      if (errorZone) errorZone.removeAttribute('hidden');
      announce(root, msg);
      if (atc) {
        atc.removeAttribute('disabled');
        atc.removeAttribute('data-loading');
      }
      if (spinner) spinner.setAttribute('hidden', '');
    }
  }

  // ---------- Qty step handler ----------

  function handleQtyStep(event) {
    var stepBtn = event.target.closest && event.target.closest('[data-pp-qty-step]');
    if (!stepBtn) return;

    event.preventDefault();

    var root = stepBtn.closest('[data-pp-buy-buttons]');
    if (!root) return;

    var direction = parseInt(stepBtn.getAttribute('data-pp-qty-step'), 10);
    if (direction !== 1 && direction !== -1) return;

    var input = root.querySelector('[data-quantity-input]');
    if (!input) return;
    if (input.hasAttribute('disabled')) return;

    var current = parseInt(input.value, 10);
    if (isNaN(current) || current < 1) current = 1;
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var maxAttr = input.getAttribute('max');
    var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
    var step = parseInt(input.getAttribute('step'), 10) || 1;

    var proposed = current + (direction * step);

    var plusBtn = root.querySelector('[data-pp-qty-step="1"]');
    var minusBtn = root.querySelector('[data-pp-qty-step="-1"]');

    if (proposed < min) {
      if (minusBtn) minusBtn.setAttribute('aria-disabled', 'true');
      return;
    }
    if (proposed > max) {
      if (plusBtn) plusBtn.setAttribute('aria-disabled', 'true');
      announce(root, 'Only ' + max + ' more available');
      return;
    }

    input.value = proposed;
    updatePriceLabel(root, proposed);

    if (minusBtn) {
      if (proposed <= min) minusBtn.setAttribute('aria-disabled', 'true');
      else minusBtn.removeAttribute('aria-disabled');
    }
    if (plusBtn) {
      if (proposed >= max) plusBtn.setAttribute('aria-disabled', 'true');
      else plusBtn.removeAttribute('aria-disabled');
    }

    var pill = root.querySelector('[data-pp-pill]');
    if (pill) {
      var readout = root.querySelector('[data-pp-qty-readout]');
      if (readout) readout.textContent = String(proposed);
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // ---------- Qty input change handler ----------

  function handleQtyInputChange(event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-quantity-input]')) return;

    var root = input.closest('[data-pp-buy-buttons]');
    if (!root) return;

    var raw = parseInt(input.value, 10);
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var maxAttr = input.getAttribute('max');
    var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
    var step = parseInt(input.getAttribute('step'), 10) || 1;

    var clamped = raw;
    var didClamp = false;
    var hitCap = false;

    if (isNaN(clamped) || clamped < min) {
      clamped = min;
      didClamp = true;
    } else if (clamped > max) {
      clamped = max;
      didClamp = true;
      hitCap = true;
    }

    if (step > 1) {
      var snapped = Math.round((clamped - min) / step) * step + min;
      if (snapped !== clamped) {
        clamped = Math.max(min, Math.min(max, snapped));
        didClamp = true;
      }
    }

    if (didClamp) {
      input.value = String(clamped);
      if (hitCap) {
        announce(root, 'Only ' + max + ' more available');
        var plusBtn = root.querySelector('[data-pp-qty-step="1"]');
        if (plusBtn) plusBtn.setAttribute('aria-disabled', 'true');
      } else {
        announce(root, 'Adjusted quantity');
      }
    }

    var pill = root.querySelector('[data-pp-pill]');
    if (pill) {
      var readout = root.querySelector('[data-pp-qty-readout]');
      if (readout) readout.textContent = input.value;
    }
    updatePriceLabel(root, parseInt(input.value, 10) || 1);
  }

  // ---------- Listener registrations ----------

  document.addEventListener('submit', handleBuyButtonsSubmit, true);
  document.addEventListener('click', handleQtyStep);
  document.addEventListener('change', handleQtyInputChange);

})();
